import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import CTALinks from 'components/CTALinks';
import PlayTriangle from 'components/Icon/PlayTriangle';
import IconButton from 'components/IconButton';
import Image from 'components/ImageV2';
import ResponsiveImage from 'components/ResponsiveImage';
import { IMAGE_RATIOS } from 'constants/images';
import { mediumUp } from 'constants/mediaQueries';
import { MODULE_TYPES } from 'constants/pdp';
import I18n from 'utils/I18n';
import { breakpoints, cn } from 'utils/Tailwind';

export const MOBILE_CARD_LAYOUT_OPTIONS = {
  CAROUSEL: 'Carousel',
  STACK: 'Stack',
};

const CardContent = ({ card, handleCTAClick, index, mobileCardLayout }) => {
  const { eyebrow, internalName, mobileMedia, subheader, supportingText } = card;
  return (
    <div
      className={cn('CardContent flex flex-col gap-2 pr-8', {
        'max-md:px-6': mobileCardLayout === MOBILE_CARD_LAYOUT_OPTIONS.STACK,
      })}
    >
      {eyebrow && <p className="text-xs font-bold uppercase tracking-widest">{card.eyebrow}</p>}
      {subheader && <p className="text-xl font-bold md:text-1xl">{card.subheader}</p>}
      {supportingText && <p className="text-base">{supportingText}</p>}
      {!isEmpty(card.buttonCTAs) && (
        <CTALinks
          dataTestId="story-module-cta-links"
          links={card.buttonCTAs}
          onClick={(linkUrl, label) => {
            handleCTAClick({
              cardPosition: index + 1,
              cardName: internalName,
              imageName: mobileMedia.file.fileName,
              linkCTAName: label,
              linkDestination: linkUrl,
            });
          }}
        />
      )}
    </div>
  );
};

const StoryModule = ({
  className,
  dataTestId,
  heading,
  internalName,
  mobileCardLayout,
  modulePosition,
  openModal,
  posterDimensions,
  showModule,
  showOnDesktop,
  showOnMobile,
  storyModuleCards,
  trackCTAClick,
}) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const isMediumUp = useMediaQuery({ query: mediumUp });

  const handleCTAClick = useCallback(
    ({ cardPosition, cardName, imageName, linkCTAName, linkDestination, isVideoModal }) => {
      trackCTAClick({
        cardName,
        cardPosition,
        imageName,
        linkCTAName,
        linkDestination,
        modulePosition,
        moduleType: MODULE_TYPES.STORY_MODULE,
        internalName,
      });

      if (isVideoModal) {
        openModal(linkDestination);
      }
    },
    [internalName, modulePosition, openModal, trackCTAClick]
  );

  const handleSlideSelect = index => {
    setActiveSlideIndex(index);
  };

  const CAROUSEL_OPTIONS = useMemo(() => {
    return {
      type: 'slide',
      arrows: false,
      gap: '0.5rem',
      mediaQuery: 'min',
      pagination: false,
      breakpoints: {
        0: {
          direction: mobileCardLayout === MOBILE_CARD_LAYOUT_OPTIONS.CAROUSEL ? 'ltr' : 'ttb',
          gap: mobileCardLayout === MOBILE_CARD_LAYOUT_OPTIONS.CAROUSEL ? '0.5rem' : '1.5rem',
          drag: mobileCardLayout === MOBILE_CARD_LAYOUT_OPTIONS.CAROUSEL,
          height: '100%',
          padding: {
            perPage: 1,
            left: '1.5rem',
            right: '1.5rem',
          },
        },
        [breakpoints.md]: {
          direction: 'ltr',
          gap: '0.5rem',
          padding: 0,
          perPage: 3,
        },
      },
    };
  }, [mobileCardLayout]);

  const isMobileHidden = !showOnMobile && !isMediumUp;
  const isDesktopHidden = !showOnDesktop && isMediumUp;

  if (isMobileHidden || isDesktopHidden || !showModule) {
    return null;
  }

  return (
    <section
      className={cn('StoryModule relative space-y-4', className)}
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      {heading && (
        <p data-testid="story-module-heading" className="text-2xl font-bold max-md:px-6">
          {heading}
        </p>
      )}

      <Splide
        key={mobileCardLayout}
        data-testid="splide"
        className=" overflow-x-visible md:mx-0 md:overflow-x-hidden"
        currentslideindex={activeSlideIndex}
        onMove={({ index }) => {
          handleSlideSelect(index);
        }}
        hasTrack={false}
        options={CAROUSEL_OPTIONS}
      >
        <SplideTrack>
          {storyModuleCards.map((card, i) => (
            <SplideSlide className="flex h-full w-full" key={card.internalName}>
              <div
                className={cn('StoryModuleCard relative flex h-full w-full flex-col gap-4', {})}
                data-testid="splide-slide"
              >
                {card.mobileMedia.file.details?.image ? (
                  <Image
                    width={card.mobileMedia.file.details.image.width}
                    height={card.mobileMedia.file.details.image.height}
                    src={card.mobileMedia.file.url}
                    alt={card.mobileMedia.description}
                    ratio={IMAGE_RATIOS.R_4x5}
                  />
                ) : (
                  <div className="relative">
                    <ResponsiveImage
                      width={posterDimensions.mobile.width}
                      height={posterDimensions.mobile.height}
                      src={card.mobileMedia.file.poster}
                      alt={card.mobileMedia.description}
                      ratio={IMAGE_RATIOS.R_4x5}
                    />
                    <div className="absolute bottom-0 left-0 right-0 top-0 inline-flex items-center justify-center">
                      <IconButton
                        className="group/play-button h-12 rounded-full !bg-black !px-6 !py-3 transition-colors hover:!bg-white"
                        data-testid="story-module-play-button"
                        onClick={() =>
                          handleCTAClick({
                            cardPosition: i + 1,
                            cardName: card.internalName,
                            linkCTAName: I18n.localize('dasherLandingPage.playButton'),
                            linkDestination: card.mobileMedia.file.url,
                            isVideoModal: true,
                          })
                        }
                      >
                        <PlayTriangle
                          className="mr-3 w-3 group-hover/play-button:invert"
                          width={12}
                        />
                        <span className="text-sm/1 font-medium uppercase tracking-widest text-white transition-colors group-hover/play-button:text-black">
                          {I18n.localize('dasherLandingPage.playButton')}
                        </span>
                      </IconButton>
                    </div>
                  </div>
                )}
                <CardContent
                  card={card}
                  handleCTAClick={handleCTAClick}
                  index={i}
                  mobileCardLayout={mobileCardLayout}
                />
              </div>
            </SplideSlide>
          ))}
        </SplideTrack>
      </Splide>
    </section>
  );
};

StoryModule.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  posterDimensions: PropTypes.shape({
    desktop: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    mobile: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  }).isRequired,
  heading: PropTypes.string,
  internalName: PropTypes.string.isRequired,
  modulePosition: PropTypes.number.isRequired,
  mobileCardLayout: PropTypes.string,
  openModal: PropTypes.func.isRequired,
  storyModuleCards: PropTypes.arrayOf(
    PropTypes.shape({
      buttonCTAs: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          openInNewWindow: PropTypes.bool,
          relativeUrl: PropTypes.string,
        })
      ),
      desktopMedia: PropTypes.shape({
        description: PropTypes.string,
        file: PropTypes.shape({
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
          url: PropTypes.string,
        }),
      }),
      mobileMedia: PropTypes.shape({
        description: PropTypes.string,
        file: PropTypes.shape({
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
          url: PropTypes.string,
        }),
      }),
      subheader: PropTypes.string,
      supportingText: PropTypes.string,
      eyebrow: PropTypes.string,
    })
  ).isRequired,
  trackCTAClick: PropTypes.func.isRequired,
  showModule: PropTypes.bool.isRequired,
  showOnMobile: PropTypes.bool.isRequired,
  showOnDesktop: PropTypes.bool.isRequired,
};

StoryModule.defaultProps = {
  backgroundColor: '',
  heading: '',
};

CardContent.propTypes = {
  card: PropTypes.object.isRequired,
  handleCTAClick: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  mobileCardLayout: PropTypes.string,
};

export default StoryModule;
